import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Sparkles, Save, Trash2, Plus, ChevronDown, Settings,
  TrendingUp, Zap, Eye, ArrowUpDown, Award, MessageSquare,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ALL_FLOW_CATS = [
  { id: 'cat-bodega', label: 'Vinos y Vermuts', section: 'alcohol' },
  { id: 'cat-cervezas', label: 'Cervezas', section: 'alcohol' },
  { id: 'cat-cocteles', label: 'Cócteles', section: 'alcohol' },
  { id: 'cat-licores', label: 'Licores', section: 'alcohol' },
  { id: 'cat-refrescos', label: 'Refrescos y Zumos', section: 'sin_alcohol' },
  { id: 'cat-cafe-plantas', label: 'Cafés', section: 'sin_alcohol' },
  { id: 'cat-chocolates-1ca5ec62', label: 'Chocolates', section: 'sin_alcohol' },
  { id: 'cat-matcha-23fd648d', label: 'Matcha', section: 'sin_alcohol' },
  { id: 'cat-infusiones-161d80f8', label: 'Infusiones', section: 'sin_alcohol' },
  { id: 'cat-picar', label: 'Para Picar', section: 'comida' },
  { id: 'cat-ensaladas', label: 'Ensaladas', section: 'comida' },
  { id: 'cat-bocadillos-tradicionales', label: 'Bocadillos Clásicos', section: 'comida' },
  { id: 'cat-bocadillos-especiales', label: 'Bocadillos Especiales', section: 'comida' },
  { id: 'cat-pizzas', label: 'Pizzas Medievales', section: 'comida' },
  { id: 'cat-dulces', label: 'Dulces', section: 'comida' },
  { id: 'cat-reposteria', label: 'Repostería', section: 'comida' },
];

const SORT_OPTIONS = [
  { value: 'default', label: 'Orden original' },
  { value: 'premium_first', label: 'Premium primero (ancla de precio)' },
  { value: 'price_high', label: 'Precio mayor → menor' },
  { value: 'price_low', label: 'Precio menor → mayor' },
  { value: 'bestseller_first', label: 'Más vendidos primero' },
];

const STEP_KEYS = [
  { key: 'party_size', label: 'Tamaño del grupo' },
  { key: 'drink_type', label: 'Tipo de bebida' },
  { key: 'drink_cat', label: 'Categoría de bebida' },
  { key: 'food_prompt', label: 'Propuesta de comida' },
  { key: 'food_cat', label: 'Categoría de comida' },
];

const TRIGGER_STEPS = [
  { value: 'drink_confirm', label: 'Después de confirmar bebida' },
  { value: 'food_confirm', label: 'Después de confirmar comida' },
];

/* ═══════════════════════════════════════ */
const GuidedFlowAdmin = () => {
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedUpsell, setExpandedUpsell] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgRes, prodsRes] = await Promise.all([
        fetch(`${API}/api/guided-flow/config`).then(r => r.json()),
        fetch(`${API}/api/products?limit=500`).then(r => r.json()),
      ]);
      setConfig(cfgRes);
      const p = Array.isArray(prodsRes) ? prodsRes : prodsRes.data || [];
      setProducts(p.filter(x => x.is_active !== false));
    } catch (e) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loc = (name) => (typeof name === 'object' ? name?.es : name) || '';
  const productsInCat = (catId) => products.filter(p => p.category_id === catId);
  const getRecForCat = (catId) => config?.recommendations?.find(r => r.category_id === catId);
  const getCopyForStep = (step) => config?.step_copy?.find(s => s.step === step) || {};

  /* ── Config updaters ── */
  const updateField = (field, value) => setConfig(p => ({ ...p, [field]: value }));

  const toggleProduct = (catId, productId) => {
    setConfig(prev => {
      const recs = [...(prev.recommendations || [])];
      const idx = recs.findIndex(r => r.category_id === catId);
      if (idx >= 0) {
        const pids = [...recs[idx].product_ids];
        const pidx = pids.indexOf(productId);
        if (pidx >= 0) pids.splice(pidx, 1); else pids.push(productId);
        recs[idx] = { ...recs[idx], product_ids: pids };
      } else {
        recs.push({ category_id: catId, product_ids: [productId], enabled: true });
      }
      return { ...prev, recommendations: recs };
    });
  };

  const updateRecField = (catId, field, value) => {
    setConfig(prev => {
      const recs = [...(prev.recommendations || [])];
      const idx = recs.findIndex(r => r.category_id === catId);
      if (idx >= 0) recs[idx] = { ...recs[idx], [field]: value };
      else recs.push({ category_id: catId, product_ids: [], enabled: true, [field]: value });
      return { ...prev, recommendations: recs };
    });
  };

  const removeRec = (catId) => {
    setConfig(prev => ({
      ...prev, recommendations: (prev.recommendations || []).filter(r => r.category_id !== catId),
    }));
  };

  const updateStepCopy = (step, field, value) => {
    setConfig(prev => {
      const copies = [...(prev.step_copy || [])];
      const idx = copies.findIndex(s => s.step === step);
      if (idx >= 0) copies[idx] = { ...copies[idx], [field]: value };
      else copies.push({ step, [field]: value });
      return { ...prev, step_copy: copies };
    });
  };

  const updateUpsell = (upsellId, field, value) => {
    setConfig(prev => {
      const moments = [...(prev.upsell_moments || [])];
      const idx = moments.findIndex(u => u.id === upsellId);
      if (idx >= 0) moments[idx] = { ...moments[idx], [field]: value };
      return { ...prev, upsell_moments: moments };
    });
  };

  const addUpsell = () => {
    const id = `upsell_${Date.now()}`;
    setConfig(prev => ({
      ...prev,
      upsell_moments: [...(prev.upsell_moments || []), {
        id, trigger_after_step: 'drink_confirm', enabled: true,
        title: 'Nuevo upsell', subtitle: '', suggested_categories: [],
        suggested_products: [], skip_label: 'No, gracias',
      }],
    }));
    setExpandedUpsell(id);
  };

  const removeUpsell = (id) => {
    setConfig(prev => ({
      ...prev, upsell_moments: (prev.upsell_moments || []).filter(u => u.id !== id),
    }));
  };

  const toggleUpsellCat = (upsellId, catId) => {
    setConfig(prev => {
      const moments = [...(prev.upsell_moments || [])];
      const idx = moments.findIndex(u => u.id === upsellId);
      if (idx < 0) return prev;
      const cats = [...(moments[idx].suggested_categories || [])];
      const ci = cats.indexOf(catId);
      if (ci >= 0) cats.splice(ci, 1); else cats.push(catId);
      moments[idx] = { ...moments[idx], suggested_categories: cats };
      return { ...prev, upsell_moments: moments };
    });
  };

  const toggleUpsellProduct = (upsellId, prodId) => {
    setConfig(prev => {
      const moments = [...(prev.upsell_moments || [])];
      const idx = moments.findIndex(u => u.id === upsellId);
      if (idx < 0) return prev;
      const prods = [...(moments[idx].suggested_products || [])];
      const pi = prods.indexOf(prodId);
      if (pi >= 0) prods.splice(pi, 1); else prods.push(prodId);
      moments[idx] = { ...moments[idx], suggested_products: prods };
      return { ...prev, upsell_moments: moments };
    });
  };

  const addCrossSell = () => {
    setConfig(prev => ({
      ...prev,
      cross_sells: [...(prev.cross_sells || []), {
        id: `xs_${Date.now()}`, trigger_category: 'cat-bodega',
        suggest_category: 'cat-picar', prompt: '', enabled: true,
      }],
    }));
  };

  const updateCrossSell = (id, field, value) => {
    setConfig(prev => {
      const rules = [...(prev.cross_sells || [])];
      const idx = rules.findIndex(r => r.id === id);
      if (idx >= 0) rules[idx] = { ...rules[idx], [field]: value };
      return { ...prev, cross_sells: rules };
    });
  };

  const removeCrossSell = (id) => {
    setConfig(prev => ({
      ...prev, cross_sells: (prev.cross_sells || []).filter(r => r.id !== id),
    }));
  };

  const updateSocialProof = (field, value) => {
    setConfig(prev => ({
      ...prev, social_proof: { ...(prev.social_proof || {}), [field]: value },
    }));
  };

  /* ── Save ── */
  const saveAll = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/guided-flow/config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (res.ok) toast.success('Configuración guardada');
      else toast.error('Error al guardar');
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-8 w-8 border-2 border-gray-200 border-t-amber-500 rounded-full" />
    </div>
  );

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'recommendations', label: 'Recomendaciones', icon: Award },
    { id: 'upselling', label: 'Upselling', icon: TrendingUp },
    { id: 'presentation', label: 'Presentación', icon: Eye },
  ];

  const sections = [
    { key: 'alcohol', label: 'Bebidas con alcohol' },
    { key: 'sin_alcohol', label: 'Bebidas sin alcohol' },
    { key: 'comida', label: 'Comida' },
  ];

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6" data-testid="guided-flow-admin">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="guided-flow-admin-title">
            <Sparkles size={24} className="text-amber-600" />
            Motor de Ventas
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configura la estrategia del camarero digital</p>
        </div>
        <Button onClick={saveAll} disabled={saving} className="gap-2" data-testid="save-flow-config-btn">
          <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6" data-testid="flow-admin-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ═══════ TAB: GENERAL ═══════ */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Welcome */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <MessageSquare size={16} /> Pantalla de bienvenida
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Título</label>
                <input type="text" placeholder="Esto no es solo tomar algo."
                  value={config?.welcome_title || ''} onChange={e => updateField('welcome_title', e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="welcome-title-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Subtítulo</label>
                <input type="text" placeholder="Estás en Besalú..."
                  value={config?.welcome_subtitle || ''} onChange={e => updateField('welcome_subtitle', e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="welcome-subtitle-input" />
              </div>
            </div>
          </div>

          {/* Cafe cutoff */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Settings size={16} /> Horario
            </h3>
            <div style={{ maxWidth: '200px' }}>
              <label className="block text-xs font-medium text-gray-500 mb-1">Corte cafetería (hora decimal)</label>
              <input type="number" step="0.5" min="0" max="24"
                value={config?.cafe_cutoff_hour ?? 12.5}
                onChange={e => updateField('cafe_cutoff_hour', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="cafe-cutoff-input" />
            </div>
          </div>

          {/* Step copy */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <MessageSquare size={16} /> Textos de cada paso
            </h3>
            <p className="text-xs text-gray-400 mb-4">Personaliza los títulos y subtítulos de cada pantalla del flujo</p>
            <div className="space-y-4">
              {STEP_KEYS.map(sk => {
                const copy = getCopyForStep(sk.key);
                return (
                  <div key={sk.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                    <div className="md:pt-2">
                      <span className="text-sm font-medium text-gray-700">{sk.label}</span>
                    </div>
                    <div>
                      <input type="text" placeholder="Título (dejar vacío = default)"
                        value={copy.title || ''} onChange={e => updateStepCopy(sk.key, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        data-testid={`step-copy-title-${sk.key}`} />
                    </div>
                    <div>
                      <input type="text" placeholder="Subtítulo"
                        value={copy.subtitle || ''} onChange={e => updateStepCopy(sk.key, 'subtitle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        data-testid={`step-copy-subtitle-${sk.key}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ TAB: RECOMMENDATIONS ═══════ */}
      {activeTab === 'recommendations' && (
        <div>
          <p className="text-sm text-gray-500 mb-4">
            Cuando el cliente entra en una categoría, muestra primero tus recomendaciones estrella.
          </p>
          {sections.map(section => (
            <div key={section.key} className="mb-8">
              <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${section.key === 'alcohol' ? 'bg-red-400' : section.key === 'sin_alcohol' ? 'bg-blue-400' : 'bg-green-400'}`} />
                {section.label}
              </h2>
              <div className="space-y-3">
                {ALL_FLOW_CATS.filter(c => c.section === section.key).map(cat => {
                  const rec = getRecForCat(cat.id);
                  const prods = productsInCat(cat.id);
                  const isExpanded = expandedCat === cat.id;
                  const selectedCount = rec?.product_ids?.length || 0;
                  return (
                    <div key={cat.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden" data-testid={`flow-cat-${cat.id}`}>
                      <button onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                        data-testid={`flow-cat-toggle-${cat.id}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-800">{cat.label}</span>
                          <span className="text-xs text-gray-400">{prods.length} productos</span>
                          {selectedCount > 0 && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`rec-count-${cat.id}`}>
                              {selectedCount} rec.
                            </span>
                          )}
                        </div>
                        <ChevronDown size={16} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      {isExpanded && (
                        <div className="border-t border-gray-100 p-4">
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Título</label>
                              <input type="text" placeholder={`Nuestra selección de ${cat.label.toLowerCase()}`}
                                value={rec?.title || ''} onChange={e => updateRecField(cat.id, 'title', e.target.value || null)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid={`rec-title-${cat.id}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Subtítulo</label>
                              <input type="text" placeholder="Lo que os recomendamos"
                                value={rec?.subtitle || ''} onChange={e => updateRecField(cat.id, 'subtitle', e.target.value || null)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid={`rec-subtitle-${cat.id}`} />
                            </div>
                          </div>
                          <p className="text-xs font-medium text-gray-500 mb-2">Productos recomendados:</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                            {prods.map(p => {
                              const isSel = rec?.product_ids?.includes(p.id);
                              return (
                                <button key={p.id} onClick={() => toggleProduct(cat.id, p.id)}
                                  className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${isSel ? 'border-amber-400 bg-amber-50' : 'border-gray-100 hover:border-gray-200'}`}
                                  data-testid={`rec-product-${p.id}`}>
                                  {(p.image_url || p.images?.[0]) && (
                                    <img src={p.image_url || p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium truncate ${isSel ? 'text-amber-800' : 'text-gray-700'}`}>{loc(p.name)}</p>
                                    <p className="text-xs text-gray-400">{p.price?.toFixed(2)}€</p>
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`}>
                                    {isSel && <span className="text-white text-xs font-bold">✓</span>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {selectedCount > 0 && (
                            <button onClick={() => removeRec(cat.id)} className="mt-3 text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-testid={`rec-clear-${cat.id}`}>
                              <Trash2 size={12} /> Quitar recomendaciones
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════ TAB: UPSELLING ═══════ */}
      {activeTab === 'upselling' && (
        <div className="space-y-6">
          {/* Upsell Moments */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Zap size={18} className="text-amber-500" /> Momentos de upsell
                </h3>
                <p className="text-xs text-gray-500 mt-1">Sugerencias que aparecen después de cada selección para escalar el ticket</p>
              </div>
              <Button onClick={addUpsell} variant="outline" size="sm" className="gap-1" data-testid="add-upsell-btn">
                <Plus size={14} /> Añadir
              </Button>
            </div>

            <div className="space-y-3">
              {(config?.upsell_moments || []).map(upsell => {
                const isExp = expandedUpsell === upsell.id;
                return (
                  <div key={upsell.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden" data-testid={`upsell-${upsell.id}`}>
                    <button onClick={() => setExpandedUpsell(isExp ? null : upsell.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${upsell.enabled ? 'bg-green-400' : 'bg-gray-300'}`} />
                        <span className="text-sm font-semibold text-gray-800">{upsell.title || 'Sin título'}</span>
                        <span className="text-xs text-gray-400">
                          {TRIGGER_STEPS.find(t => t.value === upsell.trigger_after_step)?.label || upsell.trigger_after_step}
                        </span>
                      </div>
                      <ChevronDown size={16} className={`text-gray-400 transition-transform ${isExp ? 'rotate-180' : ''}`} />
                    </button>
                    {isExp && (
                      <div className="border-t border-gray-100 p-4 space-y-4">
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={upsell.enabled}
                              onChange={e => updateUpsell(upsell.id, 'enabled', e.target.checked)}
                              className="w-4 h-4 accent-amber-500" />
                            <span className="text-sm text-gray-700">Activo</span>
                          </label>
                          <select value={upsell.trigger_after_step}
                            onChange={e => updateUpsell(upsell.id, 'trigger_after_step', e.target.value)}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                            data-testid={`upsell-trigger-${upsell.id}`}>
                            {TRIGGER_STEPS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Título (pregunta persuasiva)</label>
                            <input type="text" value={upsell.title} onChange={e => updateUpsell(upsell.id, 'title', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                              data-testid={`upsell-title-${upsell.id}`} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Subtítulo</label>
                            <input type="text" value={upsell.subtitle} onChange={e => updateUpsell(upsell.id, 'subtitle', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Texto del botón "No"</label>
                          <input type="text" value={upsell.skip_label} onChange={e => updateUpsell(upsell.id, 'skip_label', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm max-w-xs" />
                        </div>

                        {/* Suggest categories */}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-2">Categorías sugeridas (el cliente elige una):</p>
                          <div className="flex flex-wrap gap-2">
                            {ALL_FLOW_CATS.map(cat => {
                              const sel = upsell.suggested_categories?.includes(cat.id);
                              return (
                                <button key={cat.id} onClick={() => toggleUpsellCat(upsell.id, cat.id)}
                                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                    sel ? 'bg-amber-50 border-amber-400 text-amber-800' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                  }`}>
                                  {cat.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Suggest specific products */}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-2">Productos destacados (aparecen primero):</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                            {upsell.suggested_categories?.flatMap(catId => productsInCat(catId)).map(p => {
                              const sel = upsell.suggested_products?.includes(p.id);
                              return (
                                <button key={p.id} onClick={() => toggleUpsellProduct(upsell.id, p.id)}
                                  className={`flex items-center gap-2 p-2 rounded-lg border text-left text-xs ${
                                    sel ? 'border-amber-400 bg-amber-50' : 'border-gray-100'
                                  }`}>
                                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${sel ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`} />
                                  <span className="truncate">{loc(p.name)}</span>
                                  <span className="text-gray-400 ml-auto">{p.price?.toFixed(2)}€</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <button onClick={() => removeUpsell(upsell.id)}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                          <Trash2 size={12} /> Eliminar este upsell
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cross-sell Rules */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <ArrowUpDown size={18} className="text-amber-500" /> Venta cruzada
                </h3>
                <p className="text-xs text-gray-500 mt-1">Cuando eligen una categoría, sugiere algo complementario</p>
              </div>
              <Button onClick={addCrossSell} variant="outline" size="sm" className="gap-1" data-testid="add-crosssell-btn">
                <Plus size={14} /> Añadir regla
              </Button>
            </div>

            <div className="space-y-3">
              {(config?.cross_sells || []).map(rule => (
                <div key={rule.id} className="bg-white border border-gray-100 rounded-xl p-4" data-testid={`crosssell-${rule.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                    <div className="md:col-span-4">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Cuando elige...</label>
                      <select value={rule.trigger_category} onChange={e => updateCrossSell(rule.id, 'trigger_category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                        {ALL_FLOW_CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-4">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Sugerir...</label>
                      <select value={rule.suggest_category} onChange={e => updateCrossSell(rule.id, 'suggest_category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                        {ALL_FLOW_CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Mensaje</label>
                      <input type="text" placeholder="Con un buen vino va bien..." value={rule.prompt}
                        onChange={e => updateCrossSell(rule.id, 'prompt', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                    </div>
                    <div className="md:col-span-1 flex items-end gap-2">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={rule.enabled}
                          onChange={e => updateCrossSell(rule.id, 'enabled', e.target.checked)}
                          className="w-4 h-4 accent-amber-500" />
                      </label>
                      <button onClick={() => removeCrossSell(rule.id)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {(config?.cross_sells || []).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">Sin reglas de venta cruzada. Añade una para empezar.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ TAB: PRESENTATION ═══════ */}
      {activeTab === 'presentation' && (
        <div className="space-y-6">
          {/* Product sort */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <ArrowUpDown size={16} /> Orden de productos en el carrusel
            </h3>
            <p className="text-xs text-gray-400 mb-4">
              "Premium primero" usa el efecto ancla: el producto más caro se muestra primero, haciendo que el siguiente parezca más razonable.
            </p>
            <div className="space-y-2">
              {SORT_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="product_sort" value={opt.value}
                    checked={config?.product_sort === opt.value}
                    onChange={() => updateField('product_sort', opt.value)}
                    className="w-4 h-4 accent-amber-500" />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Social proof */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Award size={16} /> Social proof (prueba social)
            </h3>
            <p className="text-xs text-gray-400 mb-4">
              Etiquetas que se muestran en los productos para generar confianza y urgencia.
            </p>
            <label className="flex items-center gap-2 cursor-pointer mb-4">
              <input type="checkbox" checked={config?.social_proof?.enabled !== false}
                onChange={e => updateSocialProof('enabled', e.target.checked)}
                className="w-4 h-4 accent-amber-500" />
              <span className="text-sm text-gray-700">Activar prueba social</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Etiqueta "bestseller"</label>
                <input type="text" value={config?.social_proof?.bestseller_label || ''}
                  onChange={e => updateSocialProof('bestseller_label', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="social-bestseller-label" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Etiqueta "favorito casa"</label>
                <input type="text" value={config?.social_proof?.house_pick_label || ''}
                  onChange={e => updateSocialProof('house_pick_label', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="social-house-label" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Etiqueta "tendencia"</label>
                <input type="text" value={config?.social_proof?.trending_label || ''}
                  onChange={e => updateSocialProof('trending_label', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="social-trending-label" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedFlowAdmin;
